import { useAnalyticsContext } from "contexts/AnalyticsContext";
import type { Order } from "gql/graphql";
import { useCallback } from "react";
import {
	type AllergenType,
	DoorState,
	type OrderFeedbackRatingType,
	type OrderFeedbackReasonType,
	type Refill,
} from "types";
import type { LocalDishType, OrderSummary } from "types/mainTypes";
import {
	MixpanelActionEventsEnum,
	MixpanelTabletFieldsEnum,
	MixpanelTabletFiltersFieldsEnum,
	TabletDishDetailSectionEnum,
	TabletTutorialStepEnum,
} from "types/mixpanelTypes";
import { getUnixTimeStamp } from "../utils/time/getUnixTime";

// $scanning_duration_ms (JID-2348) replaced the scanning-started/-ended events. It is OMITTED —
// never 0 or null — when the terminal did not observe the scan start (reload mid-order, missed
// frame), so Mixpanel averages stay honest and the gap is countable as a missing property.
const scanningDurationProps = (scanningDurationMs: number | null) =>
	scanningDurationMs === null ? {} : { $scanning_duration_ms: scanningDurationMs };

export const useMixpanelTrackers = () => {
	const analyticsContext = useAnalyticsContext();

	// The finished-order events carry $order_id: it keys the deterministic $insert_id per
	// order, so a re-fire (remount, reload, ghost instance) can be deduplicated — a
	// session-keyed $insert_id is fresh per app instance and nothing can catch it (JID-2347).
	const trackFinishedOrder = useCallback(
		(
			transactionId: string,
			order: Order | null,
			orderSummary: OrderSummary | null,
			scanningDurationMs: number | null,
		) => {
			if (!(order && orderSummary)) {
				return;
			}

			const defaultPrice = orderSummary.orderPrice?.total?.default ?? null;
			const currentPrice = orderSummary.orderPrice?.total?.current ?? null;

			analyticsContext.track(MixpanelActionEventsEnum.Tablet_Order_Finished, {
				$order_id: transactionId,
				$number_of_taken_meals: order.inventory?.removed?.length,
				...(defaultPrice && { $order_total_default_price: defaultPrice }),
				...(currentPrice && { $order_total_current_price: currentPrice }),
				$order_currency: orderSummary.orderPrice?.total?.currency,
				$order_payment_method: order.payment?.method?.type,
				$order_type: order.type,
				...scanningDurationProps(scanningDurationMs),
			});
		},
		[analyticsContext],
	);

	const trackFinishedEmptyOrder = useCallback(
		(transactionId: string, scanningDurationMs: number | null) => {
			analyticsContext.track(MixpanelActionEventsEnum.Tablet_Order_Finished_Nothing_taken, {
				$order_id: transactionId,
				...scanningDurationProps(scanningDurationMs),
			});
		},
		[analyticsContext],
	);

	const trackFinishedEmptyOrderFeedback = useCallback(
		(feedbackValue: OrderFeedbackReasonType) => {
			analyticsContext.track(MixpanelActionEventsEnum.Tablet_Feedback_Nothing_Taken, {
				[MixpanelTabletFieldsEnum.Tablet_Feedback_Nothing_Taken_value]: feedbackValue,
			});
		},
		[analyticsContext],
	);

	const trackDoorStateChange = useCallback(
		(doorState: DoorState) => {
			analyticsContext.track(
				doorState === DoorState.Opened
					? MixpanelActionEventsEnum.Tablet_Door_Opened
					: MixpanelActionEventsEnum.Tablet_Door_Closed,
				{},
			);
		},
		[analyticsContext],
	);

	const trackOrderStart = useCallback(
		(order: Order | null) => {
			if (!order?.user?.id) {
				return false;
			}

			analyticsContext.identify(order.user.id, {
				name: order.user.name ?? "",
				user_id: order.user.id,
				email: order.user.email ?? null,
				created_at: order.user.createdAt ? getUnixTimeStamp(order.user.createdAt) : null,
			});
			analyticsContext.people.set({
				$name: order.user.name,
				$user_id: order.user.id,
			});
			analyticsContext.track(MixpanelActionEventsEnum.Tablet_Order_Started, {
				$order_id: order.id,
				$user_id: order.user.id,
			});
			return true;
		},
		[analyticsContext],
	);

	const trackDoorNotOpened = useCallback(
		(transactionId: string) => {
			analyticsContext.track(MixpanelActionEventsEnum.Tablet_Door_Not_Opened_In_Time, { $order_id: transactionId });
		},
		[analyticsContext],
	);

	const trackOpenDishItemDetail = useCallback(
		(
			isOpen: undefined | boolean,
			dishType: LocalDishType,
			name: {
				header: string | undefined;
				subHeader: string | undefined;
			} | null,
		) => {
			if (isOpen) {
				analyticsContext.track(MixpanelActionEventsEnum.Tablet_Close_Detail_Of_Dish, {
					$dish_type_id: dishType.id,
					$dish_type_name: name?.header,
				});
			} else {
				analyticsContext.track(MixpanelActionEventsEnum.Tablet_Open_Detail_Of_Dish, {
					$tablet_dish_detail_section: TabletDishDetailSectionEnum.About,
					$dish_type_id: dishType.id,
					$dish_type_name: name?.header,
				});
			}
		},
		[analyticsContext],
	);

	const trackDishTypeDetailTabSwitch = useCallback(
		(newValue: number, dishType: LocalDishType) => {
			analyticsContext.track(MixpanelActionEventsEnum.Tablet_Open_Detail_Of_Dish, {
				$tablet_dish_detail_section:
					newValue === 0
						? TabletDishDetailSectionEnum.About
						: newValue === 1
							? TabletDishDetailSectionEnum.Ingredients
							: TabletDishDetailSectionEnum.Instructions,
				$dish_type_name: dishType.description?.primary?.name,
				$dish_type_id: dishType.id,
			});
		},
		[analyticsContext],
	);

	const trackOrderStep1Screen = useCallback(() => {
		analyticsContext.track(MixpanelActionEventsEnum.Tablet_Open_Place_Your_Card, {});
	}, [analyticsContext]);

	/**
	 * Tracks the tutorial step for a tablet tutorial screens using the provided analytics context.
	 *
	 * @param {number} step - The current step in the tutorial.
	 * tutorial step with.
	 */
	const trackTutorialStep = useCallback(
		(step: number) => {
			let tutorialStep: TabletTutorialStepEnum;
			switch (step) {
				case 0:
					tutorialStep = TabletTutorialStepEnum.ScanTheQRCode;
					break;
				case 1:
					tutorialStep = TabletTutorialStepEnum.OpenTheFridge;
					break;
				case 2:
					tutorialStep = TabletTutorialStepEnum.TakeYourMeal;
					break;
				case 3:
					tutorialStep = TabletTutorialStepEnum.PurchaseIsFinalised;
					break;
				default:
					tutorialStep = TabletTutorialStepEnum.OpenTheFridge;
			}

			analyticsContext.track(MixpanelActionEventsEnum.Tablet_Open_Tutorial, {
				$tutorial_step: tutorialStep,
			});
		},
		[analyticsContext],
	);

	const trackDownloadAppBannerClose = useCallback(() => {
		analyticsContext.track(MixpanelActionEventsEnum.Tablet_Close_Banner_Download_app, {});
	}, [analyticsContext]);

	const trackDownloadAppBannerClick = useCallback(() => {
		analyticsContext.track(MixpanelActionEventsEnum.Tablet_Click_Banner_Download_app, {});
	}, [analyticsContext]);

	const trackFeedbackPurchaseCompleted = useCallback(
		(value: OrderFeedbackRatingType) => {
			analyticsContext.track(MixpanelActionEventsEnum.Tablet_Feedback_Purchase_Completed, {
				[MixpanelTabletFieldsEnum.Tablet_Feedback_Purchase_Completed_Value]: value,
			});
		},
		[analyticsContext],
	);

	const trackOpenReportAnIssueModal = useCallback(() => {
		analyticsContext.track(MixpanelActionEventsEnum.Tablet_Open_Report_Issue_Modal, {});
	}, [analyticsContext]);

	const trackReportIssueFeedback = useCallback(
		(value: OrderFeedbackReasonType) => {
			analyticsContext.track(MixpanelActionEventsEnum.Tablet_Feedback_Report_An_Issue, {
				[MixpanelTabletFieldsEnum.Tablet_Feedback_Report_Issue_Value]: value,
			});
		},
		[analyticsContext],
	);

	const trackManualFilterApplied = useCallback(
		(filters: AllergenType[]) => {
			analyticsContext.track(MixpanelActionEventsEnum.Tablet_Filter_Applied, {
				[MixpanelTabletFiltersFieldsEnum.Tablet_Filters_Value]: filters,
				[MixpanelTabletFiltersFieldsEnum.Tablet_Filters_Count_Value]: filters.length,
				[MixpanelTabletFiltersFieldsEnum.Tablet_Filters_Trigger_Source_Value]: "manual",
			});
		},
		[analyticsContext],
	);

	const trackFilterWindowOpen = useCallback(() => {
		analyticsContext.track(MixpanelActionEventsEnum.Tablet_Open_Filter_Window, {});
	}, [analyticsContext]);

	const trackTabletLanguageChange = useCallback(
		(fromLanguage: string, toLanguage: string, triggerSource: "manual" | "user-preference") => {
			analyticsContext.track(MixpanelActionEventsEnum.Tablet_Language_Changed, {
				$from_language: fromLanguage,
				$to_language: toLanguage,
				$language: toLanguage,
				$trigger_source: triggerSource,
			});
		},
		[analyticsContext],
	);

	const trackTabletErrorModal = useCallback(
		(state?: "open" | "close") => {
			const modalState =
				state === "open"
					? MixpanelActionEventsEnum.Tablet_Error_Modal_Open
					: MixpanelActionEventsEnum.Tablet_Error_Modal_Close;
			analyticsContext.track(modalState, {});
		},
		[analyticsContext],
	);

	const trackTabletEmptyFridgeModal = useCallback(
		(state?: "open" | "close") => {
			const modalState =
				state === "open"
					? MixpanelActionEventsEnum.Tablet_Empty_Fridge_Modal_Open
					: MixpanelActionEventsEnum.Tablet_Empty_Fridge_Modal_Close;
			analyticsContext.track(modalState, {});
		},
		[analyticsContext],
	);

	const trackNoActiveSalesPointModal = useCallback(
		(state?: "open" | "close") => {
			const modalState =
				state === "open"
					? MixpanelActionEventsEnum.Tablet_No_Active_Sales_Point_Modal_Open
					: MixpanelActionEventsEnum.Tablet_No_Active_Sales_Point_Modal_Close;
			analyticsContext.track(modalState, {});
		},
		[analyticsContext],
	);

	const trackStartingUpModal = useCallback(
		(state?: "open" | "close") => {
			const modalState =
				state === "open"
					? MixpanelActionEventsEnum.Tablet_Starting_Up_Modal_Open
					: MixpanelActionEventsEnum.Tablet_Starting_Up_Modal_Close;
			analyticsContext.track(modalState, {});
		},
		[analyticsContext],
	);

	const trackRefillStarted = useCallback(
		(refill: Refill) => {
			analyticsContext.track(MixpanelActionEventsEnum.Tablet_Refill_Started, {
				$refill_id: refill.id,
				$refill_mode: refill.mode,
				$user_id: refill.user?.id,
			});
		},
		[analyticsContext],
	);

	const trackRefillFinished = useCallback(
		(refill: Refill) => {
			analyticsContext.track(MixpanelActionEventsEnum.Tablet_Refill_Finished, {
				$refill_id: refill.id,
				$refill_mode: refill.mode,
				$user_id: refill.user?.id,
			});
		},
		[analyticsContext],
	);

	const trackRemoteRefillModal = useCallback(
		(state?: "open" | "close") => {
			const modalState =
				state === "open"
					? MixpanelActionEventsEnum.Tablet_Remote_Refill_Modal_Open
					: MixpanelActionEventsEnum.Tablet_Remote_Refill_Modal_Close;
			analyticsContext.track(modalState, {});
		},
		[analyticsContext],
	);

	return {
		trackDishTypeDetailTabSwitch: trackDishTypeDetailTabSwitch,
		trackDoorNotOpened: trackDoorNotOpened,
		trackDoorStateChange: trackDoorStateChange,
		trackDownloadAppBannerClick: trackDownloadAppBannerClick,
		trackDownloadAppBannerClose: trackDownloadAppBannerClose,
		trackFeedbackPurchaseCompleted: trackFeedbackPurchaseCompleted,
		trackFinishedOrder: trackFinishedOrder,
		trackFinishedEmptyOrder: trackFinishedEmptyOrder,
		trackFinishedEmptyOrderFeedback: trackFinishedEmptyOrderFeedback,
		trackOpenDishItemDetail: trackOpenDishItemDetail,
		trackOpenReportAnIssueModal: trackOpenReportAnIssueModal,
		trackOrderStart: trackOrderStart,
		trackOrderStep1Screen: trackOrderStep1Screen,
		trackRefillFinished: trackRefillFinished,
		trackRefillStarted: trackRefillStarted,
		trackRemoteRefillModal: trackRemoteRefillModal,
		trackReportIssueFeedback: trackReportIssueFeedback,
		trackTabletLanguageChange: trackTabletLanguageChange,
		trackTutorialStep: trackTutorialStep,
		trackManualFilterApplied: trackManualFilterApplied,
		trackFilterWindowOpen: trackFilterWindowOpen,
		trackTabletErrorModal: trackTabletErrorModal,
		trackTabletEmptyFridgeModal: trackTabletEmptyFridgeModal,
		trackNoActiveSalesPointModal: trackNoActiveSalesPointModal,
		trackStartingUpModal: trackStartingUpModal,
	};
};
